import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { User, Mail, Wallet, Edit2, Plus, Send, Link2, Copy, Check, Share2, Users, QrCode, Camera, Shield, Lock, Phone, Upload, FileCheck, Key, HardDrive } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { copyToClipboard } from '../../utils/clipboard';
import { getImageUrl } from '../../utils/imageUrl';
import GoogleDriveStatus from '../../components/GoogleDriveStatus';

const UserProfile = () => {
  const { user, fetchUser } = useAuth();
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState(user?.name || '');
  const [showAddFunds, setShowAddFunds] = useState(false);
  const [showCashTransfer, setShowCashTransfer] = useState(false);
  const [showCommissionTransfer, setShowCommissionTransfer] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showSetPin, setShowSetPin] = useState(false);
  const [showKYCUpload, setShowKYCUpload] = useState(false);
  const [addAmount, setAddAmount] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferUsername, setTransferUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [copied, setCopied] = useState(false);
  const [referralStats, setReferralStats] = useState({ total_referrals: 0 });
  const [kycFile, setKycFile] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);

  useEffect(() => {
    setNewName(user?.name || '');
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, referralRes] = await Promise.all([
          api.get('/settings'),
          api.get('/referral/stats')
        ]);
        setSettings(settingsRes.data);
        setReferralStats(referralRes.data);
      } catch (e) {
        console.error('Failed to fetch data');
      }
    };
    
    const fetchKYCStatus = async () => {
      try {
        const res = await api.get('/auth/kyc-status');
        setKycStatus(res.data);
      } catch (e) {
        // KYC endpoint may not exist yet
      }
    };
    
    fetchData();
    fetchKYCStatus();
  }, []);

  // Generate referral link
  const getReferralLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/register?ref=${user?.username}`;
  };

  // Copy referral link
  const copyReferralLink = async () => {
    const success = await copyToClipboard(getReferralLink());
    if (success) {
      setCopied(true);
      toast.success('Referral link copied!');
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error('Failed to copy');
    }
  };

  // Share referral link
  const shareReferralLink = async () => {
    const link = getReferralLink();
    const text = `Join ANANT WEB PORTAL using my referral link and get amazing digital products! 🎁`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join ANANT WEB PORTAL',
          text: text,
          url: link
        });
      } catch (err) {
        copyReferralLink();
      }
    } else {
      copyReferralLink();
    }
  };

  const handleUpdateName = async () => {
    if (!newName.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setLoading(true);
    try {
      await api.put('/auth/update-profile', { name: newName });
      toast.success('Name updated successfully');
      setIsEditingName(false);
      if (fetchUser) fetchUser();
    } catch (error) {
      toast.error('Failed to update name');
    } finally {
      setLoading(false);
    }
  };

  // Handle profile photo upload from device
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image file (JPEG, PNG, GIF, WebP)');
      return;
    }
    
    // Validate file size (5MB max for profile photos)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload image
      const uploadRes = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (uploadRes.data.success) {
        // Update profile with new photo URL
        await api.put('/auth/update-profile', { 
          profile_photo: uploadRes.data.url 
        });
        toast.success('Profile photo updated!');
        if (fetchUser) fetchUser();
      }
    } catch (error) {
      console.error('Photo upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill all fields');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('current_password', currentPassword);
      formData.append('new_password', newPassword);
      
      await api.post('/auth/change-password', formData);
      toast.success('Password changed successfully');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleSetPin = async () => {
    if (!newPin || !confirmPin) {
      toast.error('Please fill all fields');
      return;
    }
    if (newPin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }
    if (newPin.length !== 6 || !/^\d+$/.test(newPin)) {
      toast.error('PIN must be exactly 6 digits');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('pin', newPin);
      
      await api.post('/auth/set-pin', formData);
      toast.success('Login PIN set successfully! You can now login with your PIN.');
      setShowSetPin(false);
      setNewPin('');
      setConfirmPin('');
      if (fetchUser) fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleKYCUpload = async () => {
    if (!kycFile) {
      toast.error('Please select a file');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', kycFile);
      
      await api.post('/auth/upload-kyc', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('KYC document uploaded successfully. Awaiting verification.');
      setShowKYCUpload(false);
      setKycFile(null);
      
      // Refresh KYC status
      const res = await api.get('/auth/kyc-status');
      setKycStatus(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload KYC');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFunds = async () => {
    const amount = parseFloat(addAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter valid amount');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/wallet/add-funds', { amount });
      
      // Play notification sound
      try {
        const audio = new Audio('/notification.mp3');
        audio.play().catch(() => {});
      } catch (e) {}
      
      setShowAddFunds(false);
      setAddAmount('');
      toast.success(`Request to add ₹${amount} submitted. Admin will review and approve after payment verification.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (walletType) => {
    const amount = parseFloat(transferAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter valid amount');
      return;
    }
    if (!transferUsername.trim()) {
      toast.error('Please enter recipient username');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/wallet/transfer', { 
        target_username: transferUsername.trim(),
        amount,
        wallet_type: walletType
      });
      setShowCashTransfer(false);
      setShowCommissionTransfer(false);
      setTransferAmount('');
      setTransferUsername('');
      toast.success(`₹${amount} transferred successfully!`);
      if (fetchUser) fetchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="user-profile-page">
      <div className="max-w-4xl mx-auto">
        
        {/* PROFILE HEADER WITH PHOTO */}
        <div className="glass-card p-6 mb-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* Profile Photo */}
            <div className="relative">
              {user?.profile_photo ? (
                <img 
                  src={getImageUrl(user.profile_photo)} 
                  alt={user.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-primary/30"
                />
              ) : (
                <div className="w-24 h-24 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center text-white text-3xl font-bold">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
              <input
                type="file"
                ref={photoInputRef}
                onChange={handlePhotoUpload}
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                className="hidden"
                data-testid="profile-photo-input"
              />
              <button
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/80 transition-colors"
                data-testid="upload-photo-btn"
              >
                {uploadingPhoto ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 text-white" />
                )}
              </button>
            </div>
            
            {/* Profile Info */}
            <div className="flex-1 text-center md:text-left">
              {isEditingName ? (
                <div className="flex items-center gap-2 justify-center md:justify-start">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-xl font-bold"
                    autoFocus
                  />
                  <button onClick={handleUpdateName} disabled={loading} className="btn-primary px-4 py-2">
                    {loading ? '...' : 'Save'}
                  </button>
                  <button onClick={() => setIsEditingName(false)} className="px-4 py-2 bg-white/10 rounded-lg">
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 justify-center md:justify-start">
                  <h1 className="text-2xl md:text-3xl font-bold" data-testid="user-name">{user?.name}</h1>
                  <button onClick={() => setIsEditingName(true)} className="p-2 hover:bg-white/10 rounded-lg">
                    <Edit2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              <p className="text-muted-foreground">@{user?.username}</p>
              <div className="flex flex-wrap items-center gap-4 mt-2 justify-center md:justify-start">
                <span className="flex items-center gap-1 text-sm">
                  <Mail className="w-4 h-4" /> {user?.email}
                </span>
                {user?.whatsapp_number && (
                  <span className="flex items-center gap-1 text-sm">
                    <Phone className="w-4 h-4" /> {user?.whatsapp_number}
                  </span>
                )}
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="flex flex-col gap-2">
              <button
                onClick={() => setShowChangePassword(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 text-sm"
                data-testid="change-password-btn"
              >
                <Lock className="w-4 h-4" /> Change Password
              </button>
              <button
                onClick={() => setShowKYCUpload(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 text-sm"
                data-testid="kyc-upload-btn"
              >
                <FileCheck className="w-4 h-4" /> 
                {kycStatus?.status === 'verified' ? 'KYC Verified' : kycStatus?.status === 'pending' ? 'KYC Pending' : 'Upload KYC'}
              </button>
            </div>
          </div>
          
          {/* KYC Status Banner */}
          {kycStatus && (
            <div className={`mt-4 p-3 rounded-lg ${
              kycStatus.status === 'verified' ? 'bg-green-500/20 text-green-500' :
              kycStatus.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
              kycStatus.status === 'rejected' ? 'bg-red-500/20 text-red-500' :
              'bg-white/10 text-muted-foreground'
            }`}>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span className="text-sm font-semibold">
                  KYC Status: {kycStatus.status === 'verified' ? 'Verified ✓' : 
                              kycStatus.status === 'pending' ? 'Under Review' :
                              kycStatus.status === 'rejected' ? 'Rejected - Please reupload' : 'Not Submitted'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* WALLETS SECTION */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-primary" />
            My Wallets
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Cash Wallet */}
            <div className="glass-card p-6 border-2 border-primary/30">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Cash Wallet</h3>
                    <p className="text-xs text-muted-foreground">For purchases</p>
                  </div>
                </div>
              </div>
              <p className="text-4xl font-bold text-primary mb-4" data-testid="cash-wallet">
                ₹{user?.cash_wallet?.toFixed(2) || '0.00'}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAddFunds(true)}
                  className="flex-1 btn-primary flex items-center justify-center gap-2 text-sm py-2"
                  data-testid="add-funds-button"
                >
                  <Plus className="w-4 h-4" />
                  Add Funds
                </button>
                <button
                  onClick={() => setShowCashTransfer(true)}
                  className="flex-1 px-4 py-2 bg-secondary/20 text-secondary rounded-full font-semibold hover:bg-secondary/30 flex items-center justify-center gap-2 text-sm"
                  data-testid="cash-transfer-button"
                >
                  <Send className="w-4 h-4" />
                  Transfer
                </button>
              </div>
            </div>
            
            {/* Commission Wallet */}
            <div className="glass-card p-6 border-2 border-accent/30">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-accent/20 rounded-xl flex items-center justify-center">
                    <Wallet className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Commission Wallet</h3>
                    <p className="text-xs text-muted-foreground">Affiliate Network Earnings</p>
                  </div>
                </div>
              </div>
              <p className="text-4xl font-bold text-accent mb-4" data-testid="commission-wallet">
                ₹{user?.commission_wallet?.toFixed(2) || '0.00'}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCommissionTransfer(true)}
                  className="flex-1 px-4 py-2 bg-accent/20 text-accent rounded-full font-semibold hover:bg-accent/30 flex items-center justify-center gap-2 text-sm"
                  data-testid="commission-transfer-button"
                >
                  <Send className="w-4 h-4" />
                  Transfer to Member
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Can be used for 50% of purchases or member transfers only
              </p>
            </div>
          </div>
        </div>

        {/* REFERRAL LINK SECTION */}
        <div className="glass-card p-6 mb-8 border-2 border-green-500/30">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Link2 className="w-5 h-5 text-green-500" />
            Your Referral Link
          </h2>
          
          <div className="bg-white/5 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={getReferralLink()}
                readOnly
                className="flex-1 bg-transparent border-none text-sm font-mono truncate"
                data-testid="referral-link-input"
              />
              <button
                onClick={copyReferralLink}
                className={`p-2 rounded-lg transition-all ${copied ? 'bg-green-500/20 text-green-500' : 'bg-white/10 hover:bg-white/20'}`}
                data-testid="copy-referral-btn"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={copyReferralLink}
              className="flex-1 btn-primary flex items-center justify-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Copy Link
            </button>
            <button
              onClick={shareReferralLink}
              className="flex-1 px-4 py-3 bg-green-500/20 text-green-500 rounded-full font-semibold hover:bg-green-500/30 flex items-center justify-center gap-2"
            >
              <Share2 className="w-4 h-4" />
              Share
            </button>
            <a
              href={`https://wa.me/?text=Join ANANT WEB PORTAL using my referral link: ${encodeURIComponent(getReferralLink())}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 px-4 py-3 bg-[#25D366]/20 text-[#25D366] rounded-full font-semibold hover:bg-[#25D366]/30 flex items-center justify-center gap-2"
            >
              WhatsApp
            </a>
          </div>
          
          <div className="mt-4 pt-4 border-t border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Total Referrals</span>
              <span className="text-2xl font-bold text-green-500" data-testid="total-referrals">
                {referralStats.total_referrals}
              </span>
            </div>
          </div>
        </div>

        {/* Account Info */}
        <div className="glass-card p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            Account Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white/5 p-4 rounded-xl">
              <p className="text-sm text-muted-foreground">Username</p>
              <p className="font-semibold">@{user?.username}</p>
            </div>
            <div className="bg-white/5 p-4 rounded-xl">
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-semibold">{user?.email}</p>
            </div>
            <div className="bg-white/5 p-4 rounded-xl">
              <p className="text-sm text-muted-foreground">WhatsApp</p>
              <p className="font-semibold">{user?.whatsapp_number || 'Not set'}</p>
            </div>
            <div className="bg-white/5 p-4 rounded-xl">
              <p className="text-sm text-muted-foreground">Member Since</p>
              <p className="font-semibold">{new Date(user?.created_at).toLocaleDateString()}</p>
            </div>
            <div className="bg-white/5 p-4 rounded-xl">
              <p className="text-sm text-muted-foreground">Referred By</p>
              <p className="font-semibold">{user?.referral_username || 'Direct Signup'}</p>
            </div>
            <div className="bg-white/5 p-4 rounded-xl">
              <p className="text-sm text-muted-foreground">Activation Status</p>
              <p className={`font-semibold ${user?.is_activated ? 'text-green-500' : 'text-yellow-500'}`}>
                {user?.is_activated ? 'Activated ✓' : 'Not Activated'}
              </p>
            </div>
          </div>
        </div>

        {/* Connected Services */}
        <div className="glass-card p-6 mt-8 border-2 border-green-500/30">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <HardDrive className="w-5 h-5 text-green-500" />
            Connected Services
          </h2>
          <GoogleDriveStatus />
        </div>

        {/* Telegram Alerts Setup */}
        <TelegramAlertSetup />

        {/* Security Settings */}
        <div className="glass-card p-6 mt-8 border-2 border-purple-500/30">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-500" />
            Security Settings
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Change Password */}
            <div className="bg-white/5 p-4 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                    <Lock className="w-5 h-5 text-red-500" />
                  </div>
                  <div>
                    <p className="font-semibold">Password</p>
                    <p className="text-xs text-muted-foreground">Change your login password</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowChangePassword(true)}
                  className="px-4 py-2 bg-red-500/20 text-red-500 rounded-lg hover:bg-red-500/30 text-sm font-semibold"
                  data-testid="change-password-btn"
                >
                  Change
                </button>
              </div>
            </div>
            
            {/* Set/Change PIN */}
            <div className="bg-white/5 p-4 rounded-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Key className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="font-semibold">Login PIN</p>
                    <p className="text-xs text-muted-foreground">
                      {user?.login_pin ? 'PIN is set ✓' : 'Quick 6-digit login'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSetPin(true)}
                  className="px-4 py-2 bg-blue-500/20 text-blue-500 rounded-lg hover:bg-blue-500/30 text-sm font-semibold"
                  data-testid="set-pin-btn"
                >
                  {user?.login_pin ? 'Change' : 'Set PIN'}
                </button>
              </div>
            </div>
          </div>
          
          <p className="text-xs text-muted-foreground mt-4 text-center">
            You can login with either your Password or PIN for convenience
          </p>
        </div>

        {/* Change Password Modal */}
        {showChangePassword && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full" data-testid="change-password-modal">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <Lock className="w-6 h-6 text-primary" />
                Change Password
              </h2>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Current Password</label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3"
                    placeholder="Enter current password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3"
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleChangePassword}
                  disabled={loading}
                  className="flex-1 btn-primary"
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  onClick={() => {
                    setShowChangePassword(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Set PIN Modal */}
        {showSetPin && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full" data-testid="set-pin-modal">
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                <Key className="w-6 h-6 text-blue-500" />
                {user?.login_pin ? 'Change Login PIN' : 'Set Login PIN'}
              </h2>
              <p className="text-muted-foreground mb-6">
                Set a 6-digit PIN for quick login. You can use either your password or PIN to login.
              </p>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Enter 6-Digit PIN</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    value={newPin}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setNewPin(val);
                    }}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-center text-2xl tracking-widest font-mono"
                    placeholder="• • • • • •"
                    data-testid="new-pin-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Confirm PIN</label>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    value={confirmPin}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '');
                      setConfirmPin(val);
                    }}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-center text-2xl tracking-widest font-mono"
                    placeholder="• • • • • •"
                    data-testid="confirm-pin-input"
                  />
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleSetPin}
                  disabled={loading || newPin.length !== 6}
                  className="flex-1 btn-primary disabled:opacity-50"
                  data-testid="save-pin-btn"
                >
                  {loading ? 'Saving...' : 'Save PIN'}
                </button>
                <button
                  onClick={() => {
                    setShowSetPin(false);
                    setNewPin('');
                    setConfirmPin('');
                  }}
                  className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* KYC Upload Modal */}
        {showKYCUpload && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full" data-testid="kyc-upload-modal">
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                <FileCheck className="w-6 h-6 text-primary" />
                Upload KYC Document
              </h2>
              <p className="text-muted-foreground mb-6">
                Upload Aadhaar Card, PAN Card, or Driving License for verification
              </p>
              
              <div className="mb-6">
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                >
                  <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  {kycFile ? (
                    <p className="text-primary font-semibold">{kycFile.name}</p>
                  ) : (
                    <>
                      <p className="font-semibold">Click to upload</p>
                      <p className="text-sm text-muted-foreground">PNG, JPG or PDF (max 5MB)</p>
                    </>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setKycFile(e.target.files[0])}
                  className="hidden"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleKYCUpload}
                  disabled={loading || !kycFile}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {loading ? 'Uploading...' : 'Upload Document'}
                </button>
                <button
                  onClick={() => {
                    setShowKYCUpload(false);
                    setKycFile(null);
                  }}
                  className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Funds Modal */}
        {showAddFunds && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full" data-testid="add-funds-modal">
              <h2 className="text-2xl font-bold mb-6">Add Funds to Cash Wallet</h2>
              
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Amount (₹)</label>
                <input
                  type="number"
                  value={addAmount}
                  onChange={(e) => setAddAmount(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-2xl font-bold"
                  placeholder="0.00"
                  min="1"
                />
              </div>
              
              {/* QR Code and UPI for Payment */}
              <div className="mb-6 p-4 bg-white/5 rounded-xl border border-primary/30">
                <div className="flex items-center gap-2 mb-4">
                  <QrCode className="w-5 h-5 text-primary" />
                  <h3 className="font-bold">Pay via UPI</h3>
                </div>
                
                {settings?.manual_payment_qr && (
                  <div className="flex justify-center mb-4">
                    <img 
                      src={settings.manual_payment_qr} 
                      alt="Payment QR Code" 
                      className="w-48 h-48 rounded-xl border-2 border-white/20"
                      data-testid="add-funds-qr"
                    />
                  </div>
                )}
                
                {settings?.manual_payment_upi && (
                  <div className="bg-white/10 rounded-lg p-3">
                    <p className="text-sm text-muted-foreground mb-1">UPI ID</p>
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-mono font-bold text-primary" data-testid="add-funds-upi">
                        {settings.manual_payment_upi}
                      </p>
                      <button 
                        onClick={async () => {
                          const success = await copyToClipboard(settings.manual_payment_upi);
                          if (success) toast.success('UPI ID copied!');
                          else toast.error('Failed to copy');
                        }}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
                
                <p className="text-sm text-yellow-500 mt-4 text-center">
                  After payment, submit request. Admin will verify and credit your wallet.
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleAddFunds}
                  disabled={loading || !addAmount}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {loading ? 'Submitting...' : 'Submit Request'}
                </button>
                <button
                  onClick={() => {
                    setShowAddFunds(false);
                    setAddAmount('');
                  }}
                  className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cash Transfer Modal */}
        {showCashTransfer && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full" data-testid="cash-transfer-modal">
              <h2 className="text-2xl font-bold mb-6">Transfer from Cash Wallet</h2>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Recipient Username</label>
                  <input
                    type="text"
                    value={transferUsername}
                    onChange={(e) => setTransferUsername(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3"
                    placeholder="Enter username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Amount (₹)</label>
                  <input
                    type="number"
                    value={transferAmount}
                    onChange={(e) => setTransferAmount(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-xl font-bold"
                    placeholder="0.00"
                    min="1"
                  />
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                Available: <span className="text-primary font-bold">₹{user?.cash_wallet?.toFixed(2)}</span>
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => handleTransfer('cash')}
                  disabled={loading}
                  className="flex-1 btn-primary"
                >
                  {loading ? 'Transferring...' : 'Transfer'}
                </button>
                <button
                  onClick={() => {
                    setShowCashTransfer(false);
                    setTransferAmount('');
                    setTransferUsername('');
                  }}
                  className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Commission Transfer Modal */}
        {showCommissionTransfer && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full" data-testid="commission-transfer-modal">
              <h2 className="text-2xl font-bold mb-6">Transfer from Commission Wallet</h2>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Recipient Username</label>
                  <input
                    type="text"
                    value={transferUsername}
                    onChange={(e) => setTransferUsername(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3"
                    placeholder="Enter username"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Amount (₹)</label>
                  <input
                    type="number"
                    value={transferAmount}
                    onChange={(e) => setTransferAmount(e.target.value)}
                    className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-xl font-bold"
                    placeholder="0.00"
                    min="1"
                  />
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                Available: <span className="text-accent font-bold">₹{user?.commission_wallet?.toFixed(2)}</span>
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => handleTransfer('commission')}
                  disabled={loading}
                  className="flex-1 btn-primary"
                >
                  {loading ? 'Transferring...' : 'Transfer'}
                </button>
                <button
                  onClick={() => {
                    setShowCommissionTransfer(false);
                    setTransferAmount('');
                    setTransferUsername('');
                  }}
                  className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;

// ─── TELEGRAM ALERT SETUP ──────────────────────────────────────────────
function TelegramAlertSetup() {
  const [chatId, setChatId] = useState('');
  const [saved, setSaved] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/me/telegram-chat-id')
      .then((r) => { setSaved(r.data?.telegram_chat_id || ''); setChatId(r.data?.telegram_chat_id || ''); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/me/telegram-chat-id', { telegram_chat_id: chatId || null });
      setSaved(chatId);
      toast.success(chatId ? '✅ Telegram Chat ID save ho gaya!' : '✅ Telegram alerts disabled');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="glass-card p-6 mt-8 border-2 border-blue-500/30" data-testid="profile-telegram-setup">
      <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
        <span className="text-2xl">📱</span>
        Telegram Alerts Setup
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Lead360 CRM milestone reminders (3/7/15/30 days) seedha aapke Telegram pe — <b>100% FREE</b>, koi extra cost nahi!
      </p>

      {/* Step guide */}
      <div className="bg-blue-500/10 border border-blue-400/30 rounded-xl p-4 mb-4 space-y-2 text-sm">
        <div className="font-bold text-blue-300 text-xs uppercase tracking-wide">Steps to Connect:</div>
        <div className="flex items-start gap-2">
          <span className="w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center shrink-0 mt-0.5">1</span>
          <span>Telegram mein <a href="https://t.me/Anant_admin_bot" target="_blank" rel="noreferrer" className="text-blue-400 underline font-bold">@Anant_admin_bot</a> search karo</span>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center shrink-0 mt-0.5">2</span>
          <span><code className="bg-white/10 px-1 rounded">/start</code> message bhejo</span>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center shrink-0 mt-0.5">3</span>
          <span>Bot aapko ek <b>Chat ID number</b> dega — neeche paste karo</span>
        </div>
      </div>

      <div className="flex gap-3 items-center">
        <input
          value={chatId}
          onChange={(e) => setChatId(e.target.value)}
          placeholder="Telegram Chat ID (e.g. 123456789)"
          type="text"
          inputMode="numeric"
          disabled={loading}
          data-testid="profile-telegram-chat-id"
          className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm"
        />
        <button
          onClick={save}
          disabled={saving || loading || chatId === saved}
          data-testid="profile-telegram-save"
          className="px-5 py-3 rounded-xl bg-blue-600 text-white font-bold text-sm disabled:opacity-40 whitespace-nowrap"
        >
          {saving ? '…' : chatId === saved && saved ? '✓ Saved' : '💾 Save'}
        </button>
      </div>

      {saved && (
        <div className="mt-3 flex items-center gap-2 text-sm text-emerald-400">
          <span>✓</span>
          <span>Connected · Chat ID: <b>{saved}</b> · Alerts active hain</span>
          <button onClick={() => { setChatId(''); }} className="text-rose-400 text-xs underline ml-2">Remove</button>
        </div>
      )}
      {!saved && !loading && (
        <p className="mt-2 text-xs text-muted-foreground">Abhi koi Telegram alert connected nahi hai.</p>
      )}
    </div>
  );
}
